import React from 'react';
import PropTypes from 'prop-types';
import {createPortal} from 'react-dom';
import {useSelector} from "react-redux";
import {ButtonDropdown, ButtonGroup, DropdownItem, DropdownMenu, DropdownToggle, Input} from "reactstrap";
import {SUPPLIERS_LIST} from "../Supplier/SupplierTypeSelect";
import Icon from "../../componentsReusable/Icon";
import useDidMountEffect from "../../utils/useDidMountEffect";
import classnames from "classnames";
import useAfterUpdateEffect from "../../utils/useAfterUpdateEffect";
import {useTecRmiTypeDetails} from "../../contexts/TecRmiTypeDetailsContext";
import {isDesktop} from "react-device-detect";
import {useTranslation} from "react-i18next";
import TextWithTooltip from "../../componentsReusable/TextWithTooltip";
import dataLayerPush from "../../utils/dataLayerPush";

const LOCAL_STORAGE_KEY_NAME = 'tecrmi-suppliers-favorite'

export const TCD_VEHICLE_TYPE_CAR = 'car';
export const TCD_VEHICLE_TYPE_TRUCK = 'truck';

const TecRmiVehicleTypeSuppliers = (props) => {
  const { t } = useTranslation('tec_rmi_vehicle_type_suppliers')
  const typeDetailsProvider = useTecRmiTypeDetails()
  const [searchTextSupplierOther, setSearchTextSupplierOther] = React.useState('')
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const [suppliersList, setSuppliersList] = React.useState({
    favorites: [],
    integrated: [],
    others: [],
  })
  const toggleDropdownOpen = () => setDropdownOpen(!dropdownOpen)
  const suppliersIntegrated = useSelector(state => state.suppliers)

  useDidMountEffect(() => updateSupplierList())
  useAfterUpdateEffect(() => updateSupplierList(), [searchTextSupplierOther])

  const updateSupplierList = () => {
    suppliersList.favorites = []
    suppliersList.integrated = []
    suppliersList.others = []

    SUPPLIERS_LIST
      .filter(supplier => !!supplier.webCatalogTecRmiSearch)
      .forEach(supplier => {
        if(isSupplierFavorite(supplier.value)){
          suppliersList.favorites.push(supplier)
        }

        if(suppliersIntegrated.find(supplierIntegrated => supplierIntegrated.type === supplier.value)){
          suppliersList.integrated.push(supplier)
        }else{
          if(!searchTextSupplierOther){
            suppliersList.others.push(supplier)
          }else{
            const searchTextArray = searchTextSupplierOther.split(' ')

            const supplierName = supplier.value.toLowerCase()

            if(searchTextArray.some(text => supplierName.includes(text.toLowerCase()))){
              suppliersList.others.push(supplier)
            }
          }
        }
      })

    setSuppliersList({...suppliersList})
  }

  const getFavorites = () => {
    return JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY_NAME) ?? '[]')
  }

  const isSupplierFavorite = (supplierType) => getFavorites().includes(supplierType)

  const toggleSupplierFavorite = (supplierType) => {
    let favoritesList = getFavorites()
    const favoritesCount = favoritesList.length

    if(favoritesList.includes(supplierType)){
      favoritesList = favoritesList.filter(f => f !== supplierType)
    }else{
      favoritesList.push(supplierType)
    }

    localStorage.setItem(LOCAL_STORAGE_KEY_NAME, JSON.stringify(favoritesList))

    updateSupplierList()
    if(favoritesCount === 0 || getFavorites().length === 0){
      setDropdownOpen(false)
      setTimeout(() => {
        setDropdownOpen(true)
      }, 1)
    }
  }

  const getTypeDetails = () => {
    if(props?.typeDetails){
      return props?.typeDetails
    }

    if(typeDetailsProvider && !typeDetailsProvider.typeDetailsIsEmpty()){
      return typeDetailsProvider.typeDetails
    }

    return null
  }

  const supplierLink = (supplier, key) => {
    return <a
      key={key}
      className="dropdown-item py-1 d-flex flex-row flex-nowrap align-items-center"
      href={supplier.webCatalogTecRmiSearch(getTypeDetails().tcdVehicleType, getTypeDetails().tcdMakeId, getTypeDetails().tcdRangeId, getTypeDetails().tcdTypeId)}
      target={'_blank'}
      onClick={() => {
        dataLayerPush({
          event: 'go-to-supplier',
          value: supplier.value,
        })
      }}
    >
      <Icon
        className={classnames({
          'fa-star p-1': true,
          'fas text-warning': isSupplierFavorite(supplier.value),
          'far text-secondary': !isSupplierFavorite(supplier.value),
        })}
        tooltip={isDesktop ? (isSupplierFavorite(supplier.value) ? t('favorite_remove') : t('favorite_add')) : null}
        onClick={e => {
          e.preventDefault()

          toggleSupplierFavorite(supplier.value)
        }}
      />
      <span>{supplier?.label}</span>
    </a>
  }

  if(!getTypeDetails()){
    return null
  }

  return <ButtonGroup color={'light'} size={'sm'} className="flex-wrap">
    {suppliersList.favorites
      .sort((a,b) => getFavorites().indexOf(a.value) > getFavorites().indexOf(b.value) ? 1 : -1)
      .map((supplier, key) => <TextWithTooltip
        tag={'a'}
        className="btn btn-light btn-sm"
        tooltip={supplier.name}
        href={supplier.webCatalogTecRmiSearch(getTypeDetails().tcdVehicleType, getTypeDetails().tcdMakeId, getTypeDetails().tcdRangeId, getTypeDetails().tcdTypeId)}
        key={key}
        target={'_blank'}
        onClick={() => {
          dataLayerPush({
            event: 'go-to-supplier',
            value: supplier.value,
          })
        }}
      >
        <img src={supplier.logo} style={{maxHeight: '18px'}}/>
      </TextWithTooltip>)}
    <ButtonDropdown color={'light'} size={'sm'} isOpen={dropdownOpen} toggle={toggleDropdownOpen} onClick={e => e.stopPropagation()}>
      {
        suppliersList.favorites.length > 0
          ? <DropdownToggle caret color={'light'} />
          : <DropdownToggle caret color={'light'}>
            <span className="pr-1">{t('btn_text')}</span>
          </DropdownToggle>
      }
      {createPortal(<DropdownMenu style={{zIndex: 1051}} right>
        {suppliersList.integrated.length > 0 && <React.Fragment>
          {/*<DropdownItem header>{t('supplier_list.header_yours')}</DropdownItem>*/}
          {suppliersList.integrated.map((supplier, key) => supplierLink(supplier, key))}
        </React.Fragment>}

        {(suppliersList.others.length > 0 || !!searchTextSupplierOther) && <React.Fragment>
          <DropdownItem header className="d-flex flex-row flex-nowrap align-items-center justify-content-between py-1 pr-1">
            <span className="pr-2">{t('supplier_list.header_all')}</span>
            <Input
              size={'sm'}
              placeholder={t('common:search_text')}
              style={{
                width: '130px'
              }}
              onChange={e => setSearchTextSupplierOther(e.target.value)}
              value={searchTextSupplierOther}
            />
          </DropdownItem>
          <div style={{
            height: '15vh',
            overflowY: 'auto',
          }}>
            {
              suppliersList.others.length > 0
                ? suppliersList.others.map((supplier, key) => supplierLink(supplier, key))
                : <DropdownItem className="text-center">{t('supplier_list.no_data')}</DropdownItem>
            }
          </div>
        </React.Fragment>}
      </DropdownMenu>, document.body)}
    </ButtonDropdown>
  </ButtonGroup>
};

TecRmiVehicleTypeSuppliers.propTypes = {
  tcdCategoryId: PropTypes.number,
  typeDetails: PropTypes.object,
};

export default TecRmiVehicleTypeSuppliers;